import React from 'react';
import { User, ShieldCheck, Layers, Phone } from 'lucide-react';
import { Panel, FieldRow } from '@/components/responseDetail/Panel';
import { questionLabel, readable } from '@/components/responseDetail/answerModel';
import { prefChoice, prefName } from '@/components/standard2026/ContactQuestions';

const contactKind = (v) => (typeof v === 'string' && v.includes('@') ? 'Email' : 'Mobile');

function ContactPrefRows({ item }) {
  const choice = prefChoice(item.val);
  const name = prefName(item.val);
  return (
    <>
      <FieldRow label="Contact permission" testId={`customer-contact-pref-${item.qid}`}>
        {choice === 'yes' ? 'Yes — happy to be contacted' : choice === 'no' ? 'No — do not contact' : readable(item.val)}
      </FieldRow>
      {name && <FieldRow label="Name" testId={`customer-name-${item.qid}`}>{name}</FieldRow>}
    </>
  );
}

export function CustomerDetailsPanel({ items, response }) {
  const legacy = [['Name', response.name], ['Mobile', response.mobile], ['Email', response.email]].filter(([, v]) => v);
  const callback = response.redressal?.consent ? response.redressal : null;
  if (!items.length && !legacy.length && !callback) return null;
  return (
    <Panel title="Customer Details" icon={User} testId="customer-details-panel">
      {items.map((it) => {
        if (it.q.type === 'contact_preference') return <ContactPrefRows key={it.qid} item={it} />;
        if (it.q.type === 'mobile_or_email') return <FieldRow key={it.qid} label={contactKind(it.val)} testId={`customer-contact-${it.qid}`}>{readable(it.val)}</FieldRow>;
        if (it.val && typeof it.val === 'object' && !Array.isArray(it.val)) {
          return Object.entries(it.val).filter(([, v]) => v).map(([k, v]) => (
            <FieldRow key={`${it.qid}-${k}`} label={k.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())}>{readable(v)}</FieldRow>
          ));
        }
        return <FieldRow key={it.qid} label={questionLabel(it.q)}>{readable(it.val)}</FieldRow>;
      })}
      {legacy.map(([l, v]) => <FieldRow key={l} label={l}>{v}</FieldRow>)}
      {callback && (
        <div className="mt-3 rounded-xl bg-[#FEF2F2] border border-[#FECACA] p-3" data-testid="customer-callback">
          <p className="text-xs font-semibold text-[#B91C1C] flex items-center gap-1.5 mb-1"><Phone className="w-3.5 h-3.5" /> Callback requested</p>
          <p className="text-sm text-[#0F172A]">{callback.name || 'Name not provided'} · {callback.contact || 'Contact not provided'}</p>
        </div>
      )}
    </Panel>
  );
}

export function ConsentPanel({ items }) {
  if (!items.length) return null;
  return (
    <Panel title="Consent" icon={ShieldCheck} testId="consent-panel">
      {items.map((it) => (
        <FieldRow key={it.qid} label={it.label} testId={`consent-row-${it.qid}`}>{readable(it.val)}</FieldRow>
      ))}
    </Panel>
  );
}

export function ClassificationPanel({ items }) {
  if (!items.length) return null;
  return (
    <Panel title="Usage & Classification" icon={Layers} testId="classification-panel">
      {items.map((c) => (
        <div key={c.qid} className="mb-3 last:mb-0" data-testid={`classification-${c.qid}`}>
          <FieldRow label={c.name || 'Classification'}>
            {c.derived ? <span className="font-semibold">{c.derived}</span> : <span className="text-[#94A3B8]">Not mapped</span>}
          </FieldRow>
          <FieldRow label="Answer given">{readable(c.val)}</FieldRow>
          {c.children.map((ch) => <FieldRow key={ch.qid} label={questionLabel(ch.q)}>{readable(ch.val)}</FieldRow>)}
        </div>
      ))}
    </Panel>
  );
}
